import { FlowDiagram } from "@/components/how-it-works/FlowDiagram";
import { RateExplanation } from "@/components/how-it-works/RateExplanation";
import { LandingFooter } from "@/components/public/LandingFooter";
import { PublicHeader } from "@/components/public/PublicHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LiquidButton } from "@/components/ui/liquid-button";
import { BreathingGradient } from "@/components/ui/living-brand";
import { Heading } from "@/components/ui/typography";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { sfIcons } from '@/lib/sf-icons';
import { cn } from "@/lib/utils";
import { semanticTokens } from "@packages/config/theme";
import { motion } from "framer-motion";
import { Shield } from "lucide-react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const HowItWorks: React.FC = () => {
  const { t } = useTranslation("marketing");
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    // SEO optimization
    document.title = t("howItWorksPage.title");

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t("howItWorksPage.metaDescription"));
    }

    // Add structured data for FAQ
    const faqItems = ((t("howItWorksPage.structuredData", { returnObjects: true }) as any[]) || []);
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map((item: any) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [t]);

  // Steps from SSOT (marketing.json)
  const stepsData = ((t("howItWorksPage.steps", { returnObjects: true }) as any[]) || []);
  const steps = stepsData.map((step: any, index: number) => ({
    number: String(index + 1),
    title: step.title,
    description: step.description,
    icon: step.icon === "security" ? sfIcons.security : step.icon === "globe" ? sfIcons.globe : sfIcons.send,
    color: step.color || (index === 0 ? "text-success" : index === 1 ? "text-primary" : "text-purple-500"),
    bgColor: step.bgColor || (index === 0 ? "bg-success/10" : index === 1 ? "bg-primary/10" : "bg-accent/50/10"),
  }));

  // Features from SSOT (marketing.json)
  const featuresData = ((t("howItWorksPage.features", { returnObjects: true }) as any[]) || []);
  const iconMap: Record<string, typeof sfIcons.trendingUp> = {
    "trendingUp": sfIcons.trendingUp,
    "pending": sfIcons.pending,
    "security": sfIcons.security,
  };
  const features = featuresData.map((feature: any) => ({
    icon: iconMap[feature.icon] || sfIcons.trendingUp,
    title: feature.title,
    description: feature.description,
    benefits: feature.benefits || []
  }));

  return (
    <div className="relative min-h-screen flex flex-col bg-background">
      {/* Unified PublicHeader component */}
      <PublicHeader />

      <main className="flex-1">
        <div className="w-full">
          {/* Hero Section - Mobile First */}
          <section className={cn(
            "relative overflow-hidden px-4 py-8 sm:py-12 lg:py-16",
            // CANON: Hero gradient background (lavenderGlow for fintech feel)
            semanticTokens.heroGradient.lavenderGlow.tailwind,
            // Dark mode variant
            "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
          )}>
            {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
            <BreathingGradient
              variant="lavender"
              syncWithLed
              className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
            />
            <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6 sm:space-y-8">
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: 'easeOut' }}
                className="space-y-4 sm:space-y-6"
              >
                <h1 className={cn(semanticTokens.typography.displayMd, "sm:text-display-md md:text-display-lg lg:text-display-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent leading-tight")}>
                  {t("howItWorksPage.headline")}
                </h1>
                <p className={cn(semanticTokens.typography.body, "sm:text-body-lg md:text-xl", semanticTokens.fg.muted, "leading-relaxed max-w-2xl mx-auto px-2")}>
                  {t("howItWorksPage.description")}
                </p>
              </motion.div>

              {/* Mobile-first CTA buttons */}
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.24, ease: 'easeOut', delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-sm sm:max-w-none mx-auto"
              >
                <Link to="/dashboard" className="w-full sm:w-auto">
                  <LiquidButton liquidVariant="flow" size="lg" className="gap-2 w-full sm:w-auto min-h-[44px] touch-manipulation">
                    <sfIcons.calculator className="w-5 h-5" />
                    {t("howItWorksPage.cta.tryCalculator")}
                  </LiquidButton>
                </Link>
                <Link to="/auth" className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto min-h-[44px] touch-manipulation">
                    <Shield className="w-5 h-5" />
                    {t("howItWorksPage.cta.startVerification")}
                  </Button>
                </Link>
              </motion.div>
            </div>
          </section>

          {/* Steps Section - Mobile Optimized */}
          <section className={cn(
            "relative overflow-hidden px-4 py-8 sm:py-12"
          )}>
            {/* Color Lab: Mint Fresh gradient (Cash App style) */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 30% 70%, hsla(142, 71%, 45%, 0.12) 0%, transparent 50%),
                  radial-gradient(ellipse at 70% 30%, hsla(160, 84%, 39%, 0.08) 0%, transparent 50%)
                `
              }}
            />
            <div className="relative z-10 max-w-6xl mx-auto">
              <div className="text-center mb-8 sm:mb-12">
                <Heading level={2} className="sm:text-display-sm md:text-display-md mb-4">
                  {t("howItWorksPage.stepsSection.headline")}
                </Heading>
                <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                  {t("howItWorksPage.stepsSection.description")}
                </p>
              </div>

              <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.24, ease: 'easeOut', delay: index * 0.1 }}
                    className="relative"
                  >
                    <Card className="p-6 sm:p-8 h-full border-0 shadow-card hover:shadow-card-hover transition-all duration-240">
                      <CardContent className="p-0 text-center space-y-4 sm:space-y-6">
                        <div className="relative">
                          <div className={`mx-auto w-16 h-16 sm:w-20 sm:h-20 ${step.bgColor} rounded-2xl flex items-center justify-center mb-4`}>
                            <step.icon className={`w-8 h-8 sm:w-10 sm:h-10 ${step.color}`} />
                          </div>
                          <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary text-white text-sm font-bold rounded-full flex items-center justify-center">
                            {step.number}
                          </div>
                        </div>
                        <div>
                          <h3 className={cn(semanticTokens.typography.h4, "sm:text-heading-md font-bold mb-2 sm:mb-3")}>{step.title}</h3>
                          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Arrow for desktop */}
                    {index < steps.length - 1 && (
                      <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                        <sfIcons.next className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Flow Diagram - Hidden on small mobile, visible on larger screens */}
          <section className="hidden sm:block px-4 py-8 sm:py-12">
            <FlowDiagram />
          </section>

          {/* Features Grid - Mobile Optimized */}
          <section className="px-4 py-8 sm:py-12">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8 sm:mb-12">
                <Heading level={2} className="sm:text-display-sm md:text-display-md mb-4">
                  {t("howItWorksPage.featuresSection.headline")}
                </Heading>
                <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                  {t("howItWorksPage.featuresSection.description")}
                </p>
              </div>

              <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.24, ease: 'easeOut', delay: index * 0.1 }}
                  >
                    <Card className="p-6 sm:p-8 h-full border-0 shadow-card hover:shadow-card-hover transition-all duration-240 group">
                      <CardContent className="p-0 space-y-4 sm:space-y-6">
                        <div className="flex items-center gap-4 sm:flex-col sm:items-start">
                          <div className="p-3 sm:p-4 rounded-2xl bg-primary/10 flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                            <feature.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                          </div>
                          <div className="flex-1 sm:flex-none sm:w-full">
                            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{feature.title}</h3>
                            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                              {feature.description}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {feature.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <sfIcons.success className="w-4 h-4 text-success flex-shrink-0" />
                              <span className="text-muted-foreground">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Exchange Rate Details - Mobile Optimized */}
          <section className={cn(
            "relative overflow-hidden px-4 py-8 sm:py-12"
          )}>
            {/* Color Lab: Subtle Blue-Lavender gradient */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                  radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
                `
              }}
            />
            <div className="relative z-10 max-w-4xl mx-auto">
              <RateExplanation />
            </div>
          </section>

          {/* Final CTA Section - Mobile Optimized */}
          <section className="px-4 py-12 sm:py-16 lg:py-20">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.24, ease: 'easeOut' }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-3xl p-8 sm:p-12 border border-primary/20">
                <Heading level={2} className="sm:text-display-sm md:text-display-md mb-4 sm:mb-6">{t("howItWorksPage.finalCta.headline")}</Heading>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                  {t("howItWorksPage.finalCta.description")}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                  <Link to="/auth" className="w-full sm:w-auto">
                    <LiquidButton liquidVariant="flow" size="lg" className="gap-2 w-full sm:w-auto min-h-[44px] touch-manipulation">
                      <Shield className="w-5 h-5" />
                      {t("howItWorksPage.finalCta.completeVerification")}
                    </LiquidButton>
                  </Link>
                  <Link to="/dashboard" className="w-full sm:w-auto">
                    <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto min-h-[44px] touch-manipulation">
                      <sfIcons.calculator className="w-5 h-5" />
                      {t("howItWorksPage.finalCta.tryCalculatorFirst")}
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </section>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default HowItWorks;